import { HttpClient, HttpContext } from '@angular/common/http';
import { inject } from '@angular/core';
import { ResolveFn } from '@angular/router';
import { Bundle, BundleEntry, Encounter, Observation, Condition } from 'fhir/r4';
import { delay, map, catchError, of, forkJoin, tap, concatMap } from 'rxjs';
import { StateService } from '../shared/state.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { backendEndPointToken } from '../app.config';
import { DisableSortParam, LoadingUIEnabled } from '../loading.interceptor';

export const patientsRecordResolver: ResolveFn<any> = (route, state) => {
  const http = inject(HttpClient);
  const patientId = route.parent?.params['id'] || route.params['id'];
  const backendApi = inject(backendEndPointToken);

  const stateService = inject(StateService);
  const snackBar = inject(MatSnackBar);
  stateService.setCurrentEncounter(null);
  stateService.currentPatientIdFromResolver.next(patientId);

  console.log('Resolving patient data for ID:', patientId);

  const baseUrl = backendApi || 'https://elikita-server.daalitech.com';

  // Start loading in background - don't block route activation
  loadPatientResourcesInBackground(http, baseUrl, patientId, stateService, snackBar);

  // Return immediately with minimal data to allow route activation
  return of({ id: patientId, loadingInBackground: true });
}

/**
 * Loads all patient resources asynchronously in the background with batched requests.
 * Fetches in priority order with delays to reduce server load:
 * - Batch 1 (immediate): Patient, Encounters
 * - Batch 2 (300ms delay): Conditions, Observations, MedicationRequests, Allergies
 * - Batch 3 (600ms delay): ServiceRequests, Specimens, Medications, Dispenses, Administrations, ChargeItems, Invoices
 */
function loadPatientResourcesInBackground(
  http: HttpClient,
  baseUrl: string,
  patientId: string,
  stateService: StateService,
  snackBar: MatSnackBar
): void {
  const silentContext = new HttpContext().set(LoadingUIEnabled, false);
  const silentNoSortContext = silentContext.set(DisableSortParam, true);

  // BATCH 1: Critical resources - Patient & Encounters (immediate)
  const batch1$ = forkJoin([
    http.get<Bundle<any>>(`${baseUrl}/Patient?_id=${patientId}`, {
      context: silentNoSortContext
    }).pipe(
      map((bundle: Bundle<any>) => {
        const patient = bundle?.entry?.[0]?.resource ?? null;
        console.log('Successfully fetched patient from FHIR server:', patient);
        return patient;
      }),
      catchError(error => {
        console.warn('Failed to fetch from FHIR server, falling back to static data:', error);

        // Fallback to static JSON data
        return http.get("sample_fhir_patients.json").pipe(
          map((allArray: any) => {
            const foundPatient = allArray.find((element: any) => {
              // Check multiple possible ID formats for compatibility
              return element.identifier?.[0]?.value === patientId ||
                element.id === patientId ||
                element.identifier?.value === patientId;
            });

            if (foundPatient) {
              console.log('Found patient in static data:', foundPatient);
            } else {
              console.error('Patient not found in static data for ID:', patientId);
            }

            return foundPatient;
          }),
          catchError(staticError => {
            console.error('Failed to load static data as well:', staticError);
            return of(null); // Return null if both sources fail
          })
        );
      })
    ),
    // Fetch patient encounters
    http.get<Bundle<Encounter>>(`${baseUrl}/Encounter?patient=${patientId}&_count=200`, {
      context: silentContext
    }).pipe(
      map((encounters: Bundle<Encounter>) => {
        console.log('Successfully fetched encounters from FHIR server:', encounters);
        //check for active encounters
        const activeEncounters = (encounters?.entry || [])
          .map((e) => e.resource as Encounter)
          .filter((enc) => enc && ['in-progress', 'planned', 'triaged'].includes(enc.status))
          .sort((a, b) => {
            const timeA = new Date(a.period?.start || a.meta?.lastUpdated || 0).getTime();
            const timeB = new Date(b.period?.start || b.meta?.lastUpdated || 0).getTime();
            return timeB - timeA; // Descending
          });

        if (activeEncounters.length > 0) {
          const active = activeEncounters[0];
          // Ensure period.start present (fallback to current time if missing)
          if (!active.period) { (active as any).period = { start: new Date().toISOString() }; }
          else if (!active.period.start) { (active.period as any).start = new Date().toISOString(); }
          // Attach a derived participantsDisplay for richer notice messaging
          const participantsDisplay = (active.participant || [])
            .map(p => p.individual?.display || p.individual?.reference || 'Unknown')
            .filter(Boolean);
          stateService.setCurrentEncounter({
            ...active, status: active.status as Encounter['status'],
            patientId: patientId
          });

          // Notify user with encounter date and participants
          const dateText = active.period?.start ? new Date(active.period.start).toLocaleString() : 'Unknown date';
          const participantsText = participantsDisplay.length ? participantsDisplay.join(', ') : 'No participants recorded';
          snackBar.open(`Encounter already in progress from ${dateText}. Participants: ${participantsText}.`, undefined, {
            duration: 5000,
            horizontalPosition: 'center',
            verticalPosition: 'top'
          });
        }

        return encounters.entry?.map((encounter: BundleEntry<Encounter>) => encounter.resource);
      }))
  ]);

  // BATCH 2: Secondary clinical resources (300ms delay)
  const batch2$ = of(null).pipe(
    delay(300),
    concatMap(() => forkJoin([
      // Fetch patient conditions
      http.get<Bundle<Condition>>(`${baseUrl}/Condition?patient=${patientId}&_count=200`, {
        context: silentContext
      }).pipe(
        map((conditions: Bundle<Condition>) => {
          console.log('Successfully fetched conditions from FHIR server:', conditions);
          return conditions.entry?.map((c: BundleEntry<Condition>) => c.resource) || [];
        }),
        catchError(err => {
          console.warn('Failed to load conditions:', err);
          return of([]);
        })
      ),
      // Fetch patient observations
      http.get<Bundle<Observation>>(`${baseUrl}/Observation?patient=${patientId}&_count=200`, {
        context: silentContext
      }).pipe(
        map((observations: Bundle<Observation>) => {
          console.log('Successfully fetched observations from FHIR server:', observations);
          return observations.entry?.map((observation: BundleEntry<Observation>) => observation.resource) || [];
        }),
        catchError(err => {
          console.warn('Failed to load observations:', err);
          return of([]);
        })
      ),
      //medicationRequest
      http.get<Bundle<any>>(`${baseUrl}/MedicationRequest?patient=${patientId}&_count=200`, {
        context: silentContext
      }).pipe(
        map((medRequests) => medRequests.entry?.map((mr: BundleEntry<any>) => mr.resource) || [])
      ),
      //allergies
      http.get<Bundle<any>>(`${baseUrl}/AllergyIntolerance?patient=${patientId}&_count=200`, {
        context: silentContext
      }).pipe(
        map((allergies: Bundle<any>) => {
          console.log('Successfully fetched allergies from FHIR server:', allergies);
          return allergies.entry?.map((a: BundleEntry<any>) => a.resource) || [];
        }))
    ]))
  );

  // BATCH 3: Additional resources (600ms delay)
  const batch3$ = of(null).pipe(
    delay(600),
    concatMap(() => forkJoin([
      //fetch patient labRequests - service Requests filtered to laboratory type
      http.get<Bundle<any>>(`${baseUrl}/ServiceRequest?patient=${patientId}&_count=199`, {
        context: silentContext
      }).pipe(
        map((labRequests: Bundle<any>) => {
          console.log('Successfully fetched lab requests from FHIR server:', labRequests);
          return labRequests.entry?.map((lr: BundleEntry<any>) => lr.resource) || [];
        })),
      //specimens
      http.get<Bundle<any>>(`${baseUrl}/Specimen?patient=${patientId}&_count=199`, {
        context: silentContext
      }).pipe(
        map((bundle) => bundle.entry?.map((entry: BundleEntry<any>) => entry.resource) || []),
        catchError(() => of([]))
      ),
      //medications
      http.get<Bundle<any>>(`${baseUrl}/Medication?patient=${patientId}&_count=199`, {
        context: silentContext
      }).pipe(
        map((bundle) => bundle.entry?.map((entry: BundleEntry<any>) => entry.resource) || []),
        catchError(() => of([]))
      ),
      //medicationDispense
      http.get<Bundle<any>>(`${baseUrl}/MedicationDispense?patient=${patientId}&_count=199`, {
        context: silentContext
      }).pipe(
        map((bundle) => bundle.entry?.map((entry: BundleEntry<any>) => entry.resource) || []),
        catchError(() => of([]))
      ),
      //medicationAdministration
      http.get<Bundle<any>>(`${baseUrl}/MedicationAdministration?patient=${patientId}&_count=199`, {
        context: silentContext
      }).pipe(
        map((bundle) => bundle.entry?.map((entry: BundleEntry<any>) => entry.resource) || []),
        catchError(() => of([]))
      ),
      //chargeItem
      http.get<Bundle<any>>(`${baseUrl}/ChargeItem?patient=${patientId}&_count=199`, {
        context: silentContext
      }).pipe(
        map((bundle) => bundle.entry?.map((entry: BundleEntry<any>) => entry.resource) || []),
        catchError(() => of([]))
      ),
      // Invoices
      http.get<Bundle>(`${baseUrl}/Invoice?subject=Patient/${patientId}`, {
        context: silentContext
      })
        .pipe(map(b => b.entry ?? []), catchError(() => of([])))
    ]))
  );

  // Execute batches sequentially and update StateService progressively
  batch1$.pipe(
    tap(([patient, encounters]) => {
      console.log('✅ Batch 1 loaded: Patient, Encounters');

      // Update patient
      stateService.PatientResources.currentPatient.next({
        referenceId: patient.id ? `Patient/${patient.id}` : null,
        savedStatus: 'saved',
        actualResource: patient
      });

      // Update encounters
      stateService.PatientResources.encounters.next(
        (encounters || []).map((encounter: any) => ({
          referenceId: encounter.id ? `Encounter/${encounter.id}` : null,
          savedStatus: 'saved',
          actualResource: encounter
        }))
      );
    }),
    concatMap(() => batch2$),
    tap(([conditions, observations, MedicationRequest, AllergyIntolerance]) => {
      console.log('✅ Batch 2 loaded: Conditions, Observations, MedicationRequests, Allergies');

      // Update conditions
      stateService.PatientResources.condition.next(
        (conditions || []).map((condition: any) => ({
          referenceId: condition.id ? `Condition/${condition.id}` : null,
          savedStatus: 'saved',
          actualResource: condition
        }))
      );

      // Update observations
      stateService.PatientResources.observations.next(
        (observations || []).map((observation: any) => ({
          referenceId: observation.id ? `Observation/${observation.id}` : null,
          savedStatus: 'saved',
          actualResource: observation
        }))
      );

      // Update medication requests
      stateService.PatientResources.medicationRequests.next(
        (MedicationRequest || []).map((medRequest: any) => ({
          referenceId: medRequest.id ? `MedicationRequest/${medRequest.id}` : null,
          savedStatus: 'saved',
          actualResource: medRequest
        }))
      );
      // Update allergies
      stateService.PatientResources.allergyIntolerances.next(
        (AllergyIntolerance || []).map((allergy: any) => ({
          referenceId: allergy.id ? `AllergyIntolerance/${allergy.id}` : null,
          savedStatus: 'saved',
          actualResource: allergy
        }))
      );

      // Update allergies (note: no allergies in PatientResources, but keeping for future)
      // If needed, add to StateService.PatientResources
    }),
    concatMap(() => batch3$),
    tap(([serviceRequests, specimens, medications, medicationDispenses, medicationAdministrations, chargeItems, Invoices]) => {
      console.log('✅ Batch 3 loaded: ServiceRequests, Specimens, Medications, Dispenses, Administrations, ChargeItems, Invoices');

      // Update service requests
      stateService.PatientResources.serviceRequests.next(
        (serviceRequests || []).map((serviceRequest: any) => ({
          referenceId: serviceRequest.id ? `ServiceRequest/${serviceRequest.id}` : null,
          savedStatus: 'saved',
          actualResource: serviceRequest
        }))
      );

      // Update specimens
      stateService.PatientResources.specimens.next(
        (specimens || []).map((specimen: any) => ({
          referenceId: specimen.id ? `Specimen/${specimen.id}` : null,
          savedStatus: 'saved',
          actualResource: specimen
        }))
      );

      // Update medications
      stateService.PatientResources.medications.next(
        (medications || []).map((med: any) => ({
          referenceId: med.id ? `Medication/${med.id}` : null,
          savedStatus: 'saved',
          actualResource: med
        }))
      );

      // Update medication dispenses
      stateService.PatientResources.medicationDispenses.next(
        (medicationDispenses || []).map((medDispense: any) => ({
          referenceId: medDispense.id ? `MedicationDispense/${medDispense.id}` : null,
          savedStatus: 'saved',
          actualResource: medDispense
        }))
      );

      // Update medication administrations
      stateService.PatientResources.medicationAdministrations.next(
        (medicationAdministrations || []).map((medAdmin: any) => ({
          referenceId: medAdmin.id ? `MedicationAdministration/${medAdmin.id}` : null,
          savedStatus: 'saved',
          actualResource: medAdmin
        }))
      );

      // Update charge items
      stateService.PatientResources.chargeItems.next(
        (chargeItems || []).map((chargeItem: any) => ({
          referenceId: chargeItem.id ? `ChargeItem/${chargeItem.id}` : null,
          savedStatus: 'saved',
          actualResource: chargeItem
        }))
      );

      // Update invoices
      stateService.PatientResources.invoices.next(
        (Invoices || []).map((invoice: any) => ({
          referenceId: invoice.resource?.id
            ? `Invoice/${invoice.resource.id}`
            : invoice.id
              ? `Invoice/${invoice.id}`
              : null,
          savedStatus: 'saved',
          actualResource: invoice.resource ?? invoice
        }))
      );

      console.log('✅ All batches loaded successfully for patient:', patientId);
    }),
    catchError(err => {
      console.error('❌ Failed to load patient record data:', err);
      snackBar.open('Failed to load patient record data', undefined, {
        duration: 5000,
        horizontalPosition: 'center',
        verticalPosition: 'top'
      })
      return of(null); // Don't throw - just fail silently in background
    })
  ).subscribe(); // Subscribe to trigger the background loading
}

